import { api, type ApiResponse } from './api'
import { descargar, withoutEmpty } from './descarga'

/**
 * Cliente del panel de ventas.
 *
 * El panel no calcula nada: todas las cifras llegan calculadas por el servidor.
 * Recalcular un KPI en el cliente significaría dosimplementaciones de la misma
 * regla y dos respuestas distintas a un "¿de dónde sale este número?".
 */

export interface DashboardRange {
  from: string
  to: string
  days: number
}

export interface DashboardKpis {
  /** Importe facturado en el rango: cotizaciones y ventas de tienda. */
  total_sales: number
  /** Documentos del rango: cotizaciones no canceladas y ventas de tienda. */
  documents_in_range: number
  /** Documentos del rango que acabaron facturados, de los dos tipos. */
  invoiced_documents: number
  quotes_in_range: number
  invoiced_quotes: number
  /** Ventas de tienda del rango, facturadas o no. */
  store_sales_in_range: number
  invoiced_store_sales: number
  /** Solo cotizaciones: la tienda no tiene estados logísticos. */
  active_quotes: number
  conversion_rate: number
  /** Importe medio por documento facturado, no solo por cotización. */
  average_ticket: number
  follow_ups_overdue: number
}

export interface TrendPoint {
  date: string
  total: number
}

export interface ProductRank {
  reference: string
  name: string
  quantity: number
}

export interface CustomerRank {
  customer_name: string
  total: number
  quotes: number
  /** Ventas de tienda facturadas al cliente en el rango. */
  store_sales: number
  /** Cotizaciones facturadas más ventas de tienda facturadas. */
  documents: number
}

export interface AdvisorRank {
  user_id: number
  name: string
  total: number
  quotes: number
  store_sales: number
  documents: number
}

export interface PendingFollowUp {
  id: number
  customer_name: string
  document: string | null
  status: string
  next_follow_up_date: string | null
  unread_notes_count: number
}

/**
 * Un cliente dentro de un segmento.
 *
 * `customer_id` es `null` cuando la cotización no tiene cliente enlazado. En el
 * panel no se puede abrir la ficha de ese cliente, así que el nombre se enseña
 * como texto y no como enlace. El nombre por sí solo no sirve para enlazar: es una
 * copia de la cotización y dos clientes distintos pueden llamarse igual.
 */
export interface SegmentCustomer {
  customer_id: number | null
  customer_name: string
}

/** Asesor que cotizó a un cliente del segmento. */
export interface SegmentAdvisor {
  id: number
  name: string
}

/** Una fila de cliente dentro de un segmento. */
export type SegmentRow = SegmentCustomer & {
  total: number
  quotes: number
  /** Ventas de tienda del cliente, también fuera del rango si es inactivo. */
  store_sales: number
  /** Cotizaciones más ventas de tienda que cuentan para el segmento. */
  documents: number
  /** Última compra, sea cotización o venta de tienda. */
  last_sale_date: string | null
  /** Se conserva por compatibilidad con los consumidores anteriores. */
  last_quote_date: string | null
  advisors: SegmentAdvisor[]
}

/**
 * Una página de un segmento.
 *
 * `total` son todos los clientes del segmento, no los de la página: el panel los
 * enseña en grande y confundirlos hacía que un segmento con 30 clientes dijera
 * «10». `last_page` permite pintar «página 3 de 22» sin tener que pedir todas.
 */
export interface SegmentPage {
  total: number
  page: number
  per_page: number
  last_page: number
  data: SegmentRow[]
}

export interface Segments {
  new: SegmentPage
  recurring: SegmentPage
  inactive: SegmentPage
}

/**
 * Cliente ordenado por su ticket promedio.
 *
 * `average_ticket` y no `total`: es el promedio de lo facturado por cotización de
 * ese cliente, que es una cifra distinta del ranking por gasto. Un cliente con dos
 * compras de $ 5.000 tiene el mismo ticket que uno con una de $ 10.000, y en el
 * ranking por gasto el segundo va el doble que el primero.
 */
export interface CustomerAverageRank {
  customer_name: string
  average_ticket: number
  quotes: number
  store_sales: number
  documents: number
}

export interface DashboardSummary {
  range: DashboardRange
  kpis: DashboardKpis
  daily_trend: TrendPoint[]
  status_breakdown: Record<string, number>
  top_products: ProductRank[]
  least_products: ProductRank[]
  top_customers: CustomerRank[]
  top_customers_avg: CustomerAverageRank[]
  top_advisors: AdvisorRank[]
  pending_follow_ups: PendingFollowUp[]
  segments: Segments
}

export interface DashboardParams {
  date_from?: string
  date_to?: string
  /** Página de cada segmento. Se viajan por separado porque se recorren aparte. */
  page_new_customers?: number
  page_recurring_customers?: number
  page_inactive_customers?: number
}

/**
 * Las nueve exportaciones, con su etiqueta y su endpoint.
 *
 * Viven aquí y no en la vista para que un endpoint mal escrito se detecte al
 * cambiar la ruta, y no cuando alguien pulse un botón y abra un Excel vacío.
 */
export const DASHBOARD_EXPORTS = [
  { key: 'top-products', label: 'Productos más vendidos', path: '/dashboard/export/top-products' },
  { key: 'least-products', label: 'Productos menos vendidos', path: '/dashboard/export/least-products' },
  { key: 'top-customers', label: 'Clientes por total', path: '/dashboard/export/top-customers' },
  { key: 'top-customers-avg', label: 'Clientes por ticket medio', path: '/dashboard/export/top-customers-avg' },
  { key: 'top-advisors', label: 'Asesores por ventas', path: '/dashboard/export/top-advisors' },
  { key: 'new-customers', label: 'Clientes nuevos', path: '/dashboard/export/new-customers' },
  { key: 'recurring-customers', label: 'Clientes recurrentes', path: '/dashboard/export/recurring-customers' },
  { key: 'inactive-customers', label: 'Clientes inactivos', path: '/dashboard/export/inactive-customers' },
  { key: 'pending-follow-ups', label: 'Seguimientos pendientes', path: '/dashboard/export/pending-follow-ups' },
] as const

export type DashboardExportKey = (typeof DASHBOARD_EXPORTS)[number]['key']

export const dashboardApi = {
  summary(params: DashboardParams = {}) {
    return api.get<ApiResponse<DashboardSummary>>('/dashboard', { params: withoutEmpty(params) })
  },

  /**
   * Descarga una exportación.
   *
   * El rango viaja en la query porque el backend reutiliza el mismo request que
   * el panel: sin él, el `.xlsx` saldría con el mes actual aunque el usuario
   * esté mirando otro rango. La mecánica —token, nombre del fichero, errores—
   * vive en `services/descarga.ts`, compartida con el panel de marketing.
   */
  async download(path: string, params: DashboardParams = {}): Promise<void> {
    await descargar(path, withoutEmpty(params) as Record<string, unknown>)
  },
}
